import { createContext, useState, useEffect, useCallback } from "react";
import { countries, CCAsections, repositories } from "../data/mockData";
import { useLocation, useNavigate } from "react-router-dom";
import { countDataSources, yearsRange } from "../utils/global";
import { fetchAreaGroups, fetchRegions } from '../data/https';
import { getCountryRegionalHierarchy } from '../utils/m49Hierarchy';

export const FiltersContext = createContext();
export const FiltersProvider = ({ children }) => {
    const navigate = useNavigate();
    const location = useLocation();

    // FiltersProvider sits above <Routes> in App.js, so useParams() can't resolve
    // route-specific segments here - the path is parsed manually instead. Both the
    // indicators route (indicators/:section/:subCat/:countryId/:years/:scroll?) and
    // the bulk route (:type/:countryId/:years) carry years/country as path segments
    // rather than query params.
    const getRoutePathInfo = useCallback(() => {
        const parts = location.pathname.split('/').filter(Boolean);
        const [first, ...rest] = parts;

        if (first === 'indicators') {
            const [section, subCat, countryId, years, scroll] = rest;
            return { routeType: 'indicators', section, subCat, countryId, years, scroll };
        }
        // Bulk has no fixed path prefix (":type/:countryId/:years"), so it's
        // identified by elimination: three segments, not one of the other routes.
        if (first && parts.length === 3) {
            const [, countryId, years] = parts;
            return { routeType: 'bulk', countryId, years };
        }
        return { routeType: null };
    }, [location.pathname]);

    const getYearsFromUrl = useCallback(() => {
        const { years } = getRoutePathInfo();
        if (years) {
            return decodeURIComponent(years).split(',');
        }
        return [];
    }, [getRoutePathInfo]);

    const [yearRange] = useState(yearsRange());
    const [isYearValueInitialized, setIsYearValueInitialized] = useState(false);
    const [hasNavigatedAway, setHasNavigatedAway] = useState(false);
    const yearsFromUrl = getYearsFromUrl();
    const defaultStartIndex =
        yearsFromUrl.length > 0
            ? yearRange.indexOf(yearsFromUrl[0])
            : yearRange.length - 11;
    const defaultEndIndex =
        yearsFromUrl.length > 0
            ? yearRange.indexOf(yearsFromUrl[yearsFromUrl.length - 1])
            : yearRange.length - 2;

    const [yearValue, setYearValue] = useState([
        defaultStartIndex >= 0 ? defaultStartIndex : yearRange.length - 11,
        defaultEndIndex >= 0 ? defaultEndIndex : yearRange.length - 2,
    ]);

    // recalculate yearValue if URL changes
    useEffect(() => {
        if (!isYearValueInitialized && yearRange.length > 0) {
            const yearsFromUrl = getYearsFromUrl();
            if (yearsFromUrl.length > 0) {
                const startIndex = yearRange.indexOf(yearsFromUrl[0]);
                const endIndex = yearRange.indexOf(
                    yearsFromUrl[yearsFromUrl.length - 1]
                );

                if (startIndex >= 0 && endIndex >= 0) {
                    setYearValue([startIndex, endIndex]);
                }
            }
            setIsYearValueInitialized(true);
        }
    }, [location.pathname, yearRange, isYearValueInitialized, getYearsFromUrl]);

    const filteredYears = yearRange.filter(
        (year, index) => index >= yearValue[0] && index <= yearValue[1]
    );

    const [itemsList, setItemsList] = useState([]);
    const [mainCategory, setMainCategory] = useState("");
    const [activeMenu, setActiveMenu] = useState(null);
    const [subCategory, setSubCategory] = useState(null);
    const [areaGroups, setAreaGroups] = useState([]);
    const [regions, setRegions] = useState([]);
    const [anchor, setAnchor] = useState(null);
    const [countryCode, setCountryCode] = useState(null);
    const [totalIndicators, setTotalIndicators] = useState(0);
    const [dataSources, setDataSources] = useState(0);
    const [isSearchNavigation, setIsSearchNavigation] = useState(false);
    const [isDropdownChange, setIsDropdownChange] = useState(false);

    // State for managing slider protection
    const [dataLoadingComplete, setDataLoadingComplete] = useState(0);

    const [selectOptions, setSelectOptions] = useState({
        area: [],
        domain_code: [],
        repositories: [],
        themes: [],
    });
    const [valueSelected, setValueSelected] = useState({
        area: null,
        domain_code: null,
        repositories: null,
        themes: null
    });

    const [params, setParams] = useState({
        domain_code: "",
        year: [...filteredYears],
        area: "",
    });

    // Function to signal that data loading is complete
    const signalDataLoadingComplete = useCallback(() => {
        setDataLoadingComplete(prev => prev + 1);
    }, []);

    function handleChange(event, value) {
        setParams((prevParams) => {
            let newValue;

            if (event.target.name === "area") {
                if (event.target.value) {
                    const valueId = event.target.value.id;

                    // Get dynamic regional hierarchy from m49.json
                    const regionalHierarchy = getCountryRegionalHierarchy(valueId);
                    console.log('Dynamic regional hierarchy for country:', valueId, regionalHierarchy);

                    // Build area array: [country, ...all regional groupings]
                    newValue = [valueId, ...regionalHierarchy];

                    setValueSelected((prevValue) => ({
                        ...prevValue,
                        area: event.target.value,
                    }));

                    // Fetch comparison data using the first (most specific) regional grouping
                    const firstRegion = regionalHierarchy[0] || "001"; // Fallback to World if no regions

                    Promise.all([
                        fetchRegions(
                            `https://data.fao.org/api/v2/bigquery?query=SELECT%20*%20FROM%20fao-maps-review.fao_cca.mvw_compare_countries%20WHERE%20m49_code%20%3D%20%22${valueId}%22%20&output_format=json&download=false`
                            //`https://data.review.fao.org/bigquery?query=SELECT%20*%20FROM%20fao-maps-review.fao_cca.mvw_compare_countries%20WHERE%20m49_code%20%3D%20%22${valueId}%22%20&output_format=json&download=false`
                        ),
                        fetchAreaGroups(
                            `https://data.fao.org/api/v2/bigquery?query=SELECT%20*%20FROM%20fao-maps-review.fao_cca.dim_country_groups%20WHERE%20group_m49_code%20%3D%20%22${firstRegion}%22%20AND%20m49_code%20%21%3D%20%22${valueId}%22AND%20obsolete%20%21%3D%20%22True%22&output_format=json&download=false`
                            //`https://data.review.fao.org/bigquery?query=SELECT%20*%20FROM%20fao-maps-review.fao_cca.dim_country_groups%20WHERE%20group_m49_code%20%3D%20%22${firstRegion}%22%20AND%20m49_code%20%21%3D%20%22${valueId}%22AND%20obsolete%20%21%3D%20%22True%22&output_format=json&download=false`
                        ),
                    ])
                        .then(([regionsData, areaData]) => {
                            setRegions(regionsData);
                            setAreaGroups(areaData);
                        })
                        .catch((error) => {
                            console.error("Error fetching data:", error);
                        });
                    setCountryCode(event.target.value.id);
                    // Trigger navigation update when country changes
                    if (!isSearchNavigation) {
                        // Preserve the currently viewed chart in the URL like the share button
                        setAnchor((prev) => (activeMenu != null ? String(activeMenu) : null));
                        setIsDropdownChange(true);
                    }
                } else {
                    newValue = prevParams.area;
                }
            } else if (event.target.name === "repositories") {
                if (event.target.value) {
                    const valueId = Number(event.target.value.id);

                    newValue = [valueId];
                    setValueSelected((prevValue) => ({
                        ...prevValue,
                        repositories: event.target.value,
                    }));
                    // Trigger navigation update when repository category changes
                    if (!isSearchNavigation) {
                        setIsDropdownChange(true);
                    }
                } else {
                    newValue = prevParams.repositories;
                }
            } else if (event.target.name === "themes") {
                if (event.target.value) {
                    setValueSelected((prevValue) => ({
                        ...prevValue,
                        themes: event.target.value,
                    }));
                    // Update subCategory to match the selected theme
                    setSubCategory(event.target.value.slug);
                    // Remove anchor when switching themes via dropdown (but not during search navigation)
                    if (!isSearchNavigation) {
                        setAnchor(null);
                        setIsDropdownChange(true);
                    }
                } else {
                    newValue = prevParams.themes;
                }
            } else {
                if (event.target.value) {
                    newValue = event.target.value.indicators[0].items[0].domain_code; // return first element domain_code as default
                    const subCat = event.target.value.indicators[0].slug;

                    // set all items for that specific Section
                    setItemsList([...event.target.value.indicators]);
                    // Remove anchor when changing outcome/domain via dropdown
                    setAnchor(null);
                    setIsDropdownChange(true);
                    !subCategory && setSubCategory(subCat);
                    setValueSelected((prevValue) => ({
                        ...prevValue,
                        domain_code: event.target.value,
                        themes: null, // Reset themes to null so auto-selection can pick the first one
                    }));



                    // update both domain_code and item when changiong the CCA section
                    return {
                        ...prevParams,
                        [event.target.name]: newValue,
                    };
                } else {
                    newValue = prevParams.domain_code;
                }
            }

            return {
                ...prevParams,
                [event.target.name]: newValue,
            };
        });
    }

    const handleRoute = useCallback(
        (section, slug) => {
            if (section) {
                console.log("section", section)
                if (section === "indicators") {
                    let domainName = valueSelected.domain_code?.value?.toLowerCase();
                    let subCatParam = subCategory;

                    // domain_code and subCategory are populated by two independent
                    // mechanisms and can be missing separately, not just together:
                    // domain_code auto-defaults from the static CCAsections list as
                    // soon as it's available (Sidebar.jsx), while subCategory only
                    // gets set once a domain has actually gone through handleChange
                    // (which populates itemsList/selectOptions.themes). Coming from
                    // bulk/repository without ever touching the domain dropdown,
                    // domain_code can already be defaulted while subCategory is still
                    // empty - fall each back independently rather than as one pair.
                    if (!domainName && selectOptions.domain_code?.length > 0) {
                        domainName = selectOptions.domain_code[0].value?.toLowerCase();
                    }
                    if (!subCatParam) {
                        const domainForSubCat = valueSelected.domain_code || selectOptions.domain_code?.[0];
                        subCatParam = domainForSubCat?.indicators?.[0]?.slug;
                    }

                    const areaId = valueSelected.area?.id;
                    const years = params.year.join(",");
                    // Only use the explicit 'anchor' state; do not fallback to any existing fragment
                    const safeAnchor = anchor && anchor !== '0' ? String(anchor) : null;

                    // area/years should always be set by the time this fires (the app
                    // initializes params.year with a default, and callers guard on
                    // valueSelected.area) - bail rather than emit a broken path.
                    if (!domainName || !subCatParam || !areaId || !years) return;

                    const pathSegments = [
                        'indicators',
                        domainName,
                        subCatParam,
                        encodeURIComponent(areaId),
                        encodeURIComponent(years),
                    ];
                    if (safeAnchor) pathSegments.push(encodeURIComponent(safeAnchor));

                    navigate(`/${pathSegments.join('/')}`);
                } else if (section === "repository") {
                    let repositoryName =
                        valueSelected.repositories &&
                        valueSelected.repositories.label.toLowerCase();
                    let catId = valueSelected.repositories && valueSelected.repositories.id;
                    let areaId = valueSelected?.area?.id;
                    navigate(
                        `files/${section}/${repositoryName}/${catId}/${areaId}`
                    );
                } else {
                    let years = params.year.join("%2C");
                    let areaId = valueSelected?.area?.id;
                    navigate(
                        `bulk/${areaId}/${years}`
                    );
                }
            }
        },
        [valueSelected, subCategory, params, anchor, navigate, selectOptions]
    );

    // On initial mount, hydrate state from the URL's country segment if present (optional behavior)
    useEffect(() => {
        const { countryId: pathCountryId } = getRoutePathInfo();
        if (pathCountryId && !valueSelected.area) {
            const countryObj = countries.find(c => String(c.id) === String(decodeURIComponent(pathCountryId)));
            if (countryObj) {
                // Reuse existing change handler to ensure dependent state updates
                handleChange({ target: { name: 'area', value: countryObj } });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    // Handle navigation when dropdown changes occur
    useEffect(() => {
        // Don't auto-navigate if we're on the homepage
        const isHomepage = location.pathname === '/';

        if (isDropdownChange && !isSearchNavigation && !isHomepage) {
            const timer = setTimeout(() => {
                // Handle indicators navigation regardless of anchor value
                if (valueSelected.domain_code && valueSelected.area && subCategory && mainCategory === "indicators") {
                    handleRoute("indicators");
                    setIsDropdownChange(false);
                }
                // Handle repository navigation
                else if (valueSelected.area && valueSelected.repositories && mainCategory === "repository") {
                    handleRoute("repository");
                    setIsDropdownChange(false);
                }
                // Handle bulk navigation
                else if (valueSelected.area && mainCategory === "bulk") {
                    handleRoute("bulk");
                    setIsDropdownChange(false);
                }
            }, 150);
            return () => clearTimeout(timer);
        }

        // Reset the dropdown change flag if we're on the homepage to prevent stale state
        if (isHomepage && isDropdownChange) {
            setIsDropdownChange(false);
        }
    }, [isDropdownChange, isSearchNavigation, valueSelected.domain_code, valueSelected.area, valueSelected.repositories, subCategory, mainCategory, handleRoute, location.pathname]);

    const resetParams = useCallback(() => {
        setParams((prevParams) => ({
            ...prevParams,
            domain_code: "",
            year: [...filteredYears],
            area: "",
        }));

        setValueSelected((prevValue) => ({
            ...prevValue,
            area: null,
            domain_code: null,
            repositories: null,
            themes: null
        }));

        setMainCategory("");
        setItemsList([]);
        setSubCategory(null);
        setAnchor(null);
        setAreaGroups([]);
        setRegions([]);
        setYearValue([
            defaultStartIndex >= 0 ? defaultStartIndex : yearRange.length - 10,
            defaultEndIndex >= 0 ? defaultEndIndex : yearRange.length - 1,
        ])
    }, [filteredYears, defaultStartIndex, defaultEndIndex, yearRange.length]);

    // Track navigation to reset state when returning to homepage
    useEffect(() => {
        const isHomepage = location.pathname === '/' && location.search === '';

        if (!isHomepage) {
            // User navigated away from homepage
            setHasNavigatedAway(true);
        } else if (isHomepage && hasNavigatedAway) {
            // User returned to homepage after navigating away
            resetParams();
            setHasNavigatedAway(false);
        }
    }, [location.pathname, location.search, hasNavigatedAway, resetParams]);

    useEffect(() => {
        const areaOptions = countries;
        const sectionOptions = CCAsections;
        const mainCategoryOptions = repositories;

        setSelectOptions({
            area: areaOptions,
            domain_code: sectionOptions,
            repositories: mainCategoryOptions,
            themes: itemsList
        });
    }, [itemsList]);

    const countItems = (data) => {
        return data.reduce((acc, section) => {
            return acc + section.indicators.reduce((indicatorAcc, indicator) => {
                return indicatorAcc + indicator.items.length;
            }, 0);
        }, 0);
    };

    useEffect(() => {
        setTotalIndicators(countItems(CCAsections));
        setDataSources(countDataSources())
    }, []);

    return (
        <FiltersContext.Provider
            value={{
                params,
                setParams,
                itemsList,
                subCategory,
                setSubCategory,
                mainCategory,
                setMainCategory,
                valueSelected,
                resetParams,
                setItemsList,
                setValueSelected,
                areaGroups,
                regions,
                setRegions,
                selectOptions,
                activeMenu,
                setActiveMenu,
                handleRoute,
                handleChange,
                yearRange,
                yearValue,
                setYearValue,
                anchor,
                setAnchor,
                countryCode,
                totalIndicators,
                dataSources,
                dataLoadingComplete,
                signalDataLoadingComplete,
                isSearchNavigation,
                setIsSearchNavigation,
                setIsDropdownChange
            }}
        >
            {children}
        </FiltersContext.Provider>
    );
};
